import React, { useState, useEffect } from 'react';
import { ClientEntity } from '../types';
import { useStore } from '../store/useStore';
import { db } from '../services/db';
import { Save, Loader2, FolderOpen } from 'lucide-react';
import { toast } from 'sonner';

import { IdentidadeVisual } from './modules/assets/IdentidadeVisual';
import { GaleriaVideos } from './modules/assets/GaleriaVideos';
import { GaleriaFotos } from './modules/assets/GaleriaFotos';
import { ProdutosResultados } from './modules/assets/ProdutosResultados';
import { ProvasSociais } from './modules/assets/ProvasSociais';
import { MateriaisApoio } from './modules/assets/MateriaisApoio';

interface AssetsDashboardProps {
  client: ClientEntity;
}

export const AssetsDashboard: React.FC<AssetsDashboardProps> = ({ client }) => {
  const [activeTab, setActiveTab] = useState<'identidade' | 'videos' | 'fotos' | 'produtos' | 'provas' | 'apoio'>('identidade');
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const store = useStore();

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const data = await db.getClientBusinessBrain(client.id);
        if (data) {
          if (data.branding_data) store.setBrandingData(data.branding_data);
          if (data.media_library) store.setMediaLibrary(data.media_library);
          if (data.social_proofs) store.setSocialProofs(data.social_proofs);
          if (data.support_materials) store.setSupportMaterials(data.support_materials);
        }
      } catch (err) {
        console.error('Error loading business brain:', err);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client.id]);

  const handleSave = async () => {
    try {
      setIsSaving(true);
      await db.saveClientBusinessBrain(client.id, store);
      toast.success('Ativos salvos com sucesso!');
    } catch (err) {
      console.error(err);
      toast.error('Erro ao salvar ativos.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-20">
        <Loader2 className="animate-spin text-gray-400" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900 tracking-tight flex items-center gap-2">
            <FolderOpen size={24} className="text-black" /> Central de Ativos & Materiais
          </h2>
          <p className="text-gray-500 mt-1">
            Matéria-prima da IA: Logos, cores, vídeos brutos e provas sociais.
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="bg-black hover:bg-gray-900 disabled:bg-gray-400 text-white px-6 py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 shadow-sm shrink-0"
        >
          {isSaving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          {isSaving ? 'Salvando...' : 'Salvar Ativos'}
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="flex overflow-x-auto border-b border-gray-200 hide-scrollbar bg-gray-50/50">
          <button
            onClick={() => setActiveTab('identidade')}
            className={`px-6 py-4 text-sm font-medium whitespace-nowrap transition-colors relative ${
              activeTab === 'identidade' ? 'text-black bg-white' : 'text-gray-500 hover:text-gray-800'
            }`}
          >
            Identidade & Marca
            {activeTab === 'identidade' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-black" />}
          </button>
          <button
            onClick={() => setActiveTab('videos')}
            className={`px-6 py-4 text-sm font-medium whitespace-nowrap transition-colors relative ${
              activeTab === 'videos' ? 'text-black bg-white' : 'text-gray-500 hover:text-gray-800'
            }`}
          >
            Galeria de Vídeos
            {activeTab === 'videos' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-black" />}
          </button>
          <button
            onClick={() => setActiveTab('fotos')}
            className={`px-6 py-4 text-sm font-medium whitespace-nowrap transition-colors relative ${
              activeTab === 'fotos' ? 'text-black bg-white' : 'text-gray-500 hover:text-gray-800'
            }`}
          >
            Galeria de Fotos
            {activeTab === 'fotos' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-black" />}
          </button>
          <button
            onClick={() => setActiveTab('produtos')}
            className={`px-6 py-4 text-sm font-medium whitespace-nowrap transition-colors relative ${
              activeTab === 'produtos' ? 'text-black bg-white' : 'text-gray-500 hover:text-gray-800'
            }`}
          >
            Produtos & Resultados
            {activeTab === 'produtos' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-black" />}
          </button>
          <button
            onClick={() => setActiveTab('provas')}
            className={`px-6 py-4 text-sm font-medium whitespace-nowrap transition-colors relative ${
              activeTab === 'provas' ? 'text-black bg-white' : 'text-gray-500 hover:text-gray-800'
            }`}
          >
            Provas Sociais (Prints)
            {activeTab === 'provas' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-black" />}
          </button>
          <button
            onClick={() => setActiveTab('apoio')}
            className={`px-6 py-4 text-sm font-medium whitespace-nowrap transition-colors relative ${
              activeTab === 'apoio' ? 'text-black bg-white' : 'text-gray-500 hover:text-gray-800'
            }`}
          >
            Materiais de Apoio
            {activeTab === 'apoio' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-black" />}
          </button>
        </div>
        
        <div className="p-6 md:p-8">
          {activeTab === 'identidade' && <IdentidadeVisual />}
          {activeTab === 'videos' && <GaleriaVideos />}
          {activeTab === 'fotos' && <GaleriaFotos />}
          {activeTab === 'produtos' && <ProdutosResultados />}
          {activeTab === 'provas' && <ProvasSociais />}
          {activeTab === 'apoio' && <MateriaisApoio />}
        </div>
      </div>
    </div>
  );
};
